import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Power, AlertOctagon } from "lucide-react";
import { Modal } from "../ui/index";

/**
 * Modal affichée quand un utilisateur commande manuellement un actionneur
 * qui est inclus dans des automatisations actives ET/OU contredit par un événement actif.
 *
 * Props:
 *   isOpen, onClose
 *   actionneur: { sn, type }
 *   action: "Activer" | "Désactiver"
 *   automations: [{ _id, nom, actionneurs, creneaux }]
 *   eventConflicts: [{ evenementId, evenementNom, actionEvenement, actionDemandee }] (optionnel)
 *   onConfirm({ pauseAutoIds, force }) — appelé à la confirmation
 */
export default function ManualOverrideModal({ isOpen, onClose, actionneur, action, automations = [], eventConflicts = [], onConfirm }) {
  const { t } = useTranslation();
  const [pauseIds, setPauseIds] = useState(() => automations.map(a => a._id));
  const hasEventConflict = eventConflicts && eventConflicts.length > 0;

  const handleConfirm = () => {
    onConfirm({ pauseAutoIds: pauseIds, force: hasEventConflict });
    onClose();
  };

  const handleSkip = () => {
    onConfirm({ pauseAutoIds: [], force: hasEventConflict });
    onClose();
  };

  if (!isOpen || !actionneur) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={hasEventConflict ? "Conflit avec un évènement actif" : t('manualOverride.title', 'Automatisations actives détectées')}
      size="sm"
    >
      <div className="space-y-4">
        {/* Bloc évènements en conflit (priorité) */}
        {hasEventConflict && (
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
            <AlertOctagon size={20} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Évènement actif sur {actionneur.sn}
              </p>
              <p className="text-xs text-red-600 mt-1">
                Cet évènement demande l'action contraire à celle que vous venez de déclencher. Si vous forcez, l'actionneur va exécuter votre commande mais le prochain cycle capteur risque de la ré-inverser.
              </p>
              <ul className="mt-2 space-y-1">
                {eventConflicts.map((c, i) => (
                  <li key={i} className="text-[11px] text-red-700 bg-white border border-red-200 rounded-md px-2 py-1">
                    <span className="font-semibold">{c.evenementNom}</span>
                    {" — "}
                    <span className="font-mono">veut {c.actionEvenement}</span>
                    {" / "}
                    <span className="font-mono">vous demandez {c.actionDemandee}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Warning autos */}
        {automations.length > 0 && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {t('manualOverride.warning', "L'actionneur {{sn}} est utilisé par {{count}} automatisation(s) active(s).", {
                  sn: actionneur.sn,
                  count: automations.length
                })}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                {t('manualOverride.warningDesc', "Votre commande manuelle pourrait être contredite par ces automatisations. Souhaitez-vous les mettre en pause ?")}
              </p>
            </div>
          </div>
        )}

        {/* Action résumé */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
          <Power size={16} className={action === "Activer" ? "text-green-500" : "text-red-500"} />
          <span className="text-sm font-medium text-slate-700">
            {action === "Activer" ? t('manualOverride.activate', 'Activer') : t('manualOverride.deactivate', 'Désactiver')} {actionneur.sn}
          </span>
          <span className="text-xs bg-gray-200 text-slate-600 px-2 py-0.5 rounded-full">{actionneur.type}</span>
        </div>

        {/* Liste des automatisations */}
        {automations.length > 0 && (
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">
            {t('manualOverride.selectToPause', 'Automatisations à mettre en pause :')}
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {automations.map(auto => {
              const isChecked = pauseIds.includes(auto._id);
              const creneauxDesc = (auto.creneaux || []).map(c => `${c.debut}→${c.fin}`).join(', ');
              return (
                <label
                  key={auto._id}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    isChecked
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setPauseIds(prev =>
                      prev.includes(auto._id)
                        ? prev.filter(id => id !== auto._id)
                        : [...prev, auto._id]
                    )}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{auto.nom}</p>
                    {creneauxDesc && (
                      <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{creneauxDesc}</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        )}

        {/* Boutons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
            {t('manualOverride.cancel', 'Annuler')}
          </button>
          {automations.length > 0 && (
            <button onClick={handleSkip}
              className={`px-4 py-2 text-white rounded-xl text-sm font-medium transition-colors ${
                hasEventConflict
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-slate-600 hover:bg-slate-700'
              }`}>
              {hasEventConflict ? 'Forcer sans pause' : t('manualOverride.executeOnly', 'Exécuter sans pause')}
            </button>
          )}
          <button onClick={handleConfirm}
            className={`px-4 py-2 text-white rounded-xl text-sm font-medium transition-colors ${
              hasEventConflict
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-amber-600 hover:bg-amber-700'
            }`}>
            {hasEventConflict
              ? (automations.length > 0 ? 'Forcer + Pause autos' : 'Forcer malgré l\'évènement')
              : t('manualOverride.executeAndPause', 'Exécuter + Pause')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
